import { Inmate } from '@reliance/types/models/Inmate';
import { TextMessage } from '@reliance/types/models/TextMessage';
import moment from 'moment';
import React, { useEffect, useMemo, useRef } from 'react';
import { Card } from 'react-bootstrap';
import { useInmateTextMessagesByInmateId } from '../../hooks/TextMessage';
import { useNotifyTabLoading } from '../../hooks/useTabLoadingReporter';
import { useServerTableFilterState } from '../../table/useServerTableFilterState';
import Table, { COLUMNS, mapFilters } from '../../text-message/Table';

type InmateTextMessagesProps = {
  inmate: Inmate;
  tabSelected?: boolean;
  refreshTrigger?: number;
};

const defaultStartDate = () => moment().subtract(1, 'year').startOf('day').format('YYYY-MM-DD HH:mm:ss');
const defaultEndDate = () => moment().endOf('day').format('YYYY-MM-DD HH:mm:ss');

const InmateTextMessages: React.FC<InmateTextMessagesProps> = (props) => {
  const { inmate, tabSelected, refreshTrigger } = props;

  const initialColumnFilters = useMemo(
    () => [
      {
        id: 'createdDate',
        value: {
          startDate: moment(defaultStartDate()).format('YYYY/MM/DD'),
          endDate: moment(defaultEndDate()).format('YYYY/MM/DD'),
        },
      },
    ],
    []
  );

  const baseRecord = useMemo(() => ({}), []);
  const tableState = useServerTableFilterState(COLUMNS, baseRecord, {
    initialPagination: { pageIndex: 0, pageSize: 100 },
    initialSorting: [{ id: 'createdDate', desc: true }],
    initialColumnFilters,
  });

  const filters = useMemo(() => {
    const mapped = mapFilters(tableState.mergedRecord, COLUMNS, () => ({
      startDate: defaultStartDate(),
      endDate: defaultEndDate(),
    }));
    return {
      ...mapped,
      inmates: String(inmate.id),
    };
  }, [tableState.mergedRecord, inmate.id]);

  const textMessagesResult = useInmateTextMessagesByInmateId(
    null,
    filters
  ) as {
    result?: { data: TextMessage[]; total: number; page?: number; perPage?: number };
    loading: boolean;
    execute: (id: number) => void;
  };

  const executeRef = useRef(textMessagesResult.execute);
  executeRef.current = textMessagesResult.execute;

  useEffect(() => {
    if (!tabSelected || !inmate.id) {
      return;
    }
    void executeRef.current(inmate.id);
  }, [tabSelected, inmate.id, tableState.mergedRecord, refreshTrigger]);

  useNotifyTabLoading('text-messages', textMessagesResult.loading);

  const textMessages = textMessagesResult.result?.data || [];
  const total = textMessagesResult.result?.total || 0;
  const page = textMessagesResult.result?.page ?? tableState.pagination.pageIndex + 1;
  const perPage = textMessagesResult.result?.perPage ?? tableState.pagination.pageSize;

  return (
    <Card>
      <Card.Body>
        <Table
          data={textMessages}
          loading={textMessagesResult.loading}
          paginationMode="server"
          page={page}
          totalPages={Math.ceil(total / perPage) || 1}
          total={total}
          perPage={perPage}
          visibleColumns={['createdDate', 'direction', 'status', 'customerAni', 'customerName', 'message']}
          initialColumnFilters={initialColumnFilters}
          serverPagination={tableState.serverPagination}
          onServerPaginationChange={tableState.onServerPaginationChange}
          serverSorting={tableState.serverSorting}
          onServerSortingChange={tableState.onServerSortingChange}
          serverColumnFilters={tableState.serverColumnFilters}
          onServerColumnFiltersChange={tableState.onServerColumnFiltersChange}
        />
      </Card.Body>
    </Card>
  );
};

export default InmateTextMessages;
